<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ContactSearch from './ContactSearch.vue';

const props = defineProps({
  calendar: {
    type: Object,
    default: null,
  },
  calendars: {
    type: Array,
    default: () => [],
  },
  appointment: {
    type: Object,
    default: null,
  },
  initialDate: {
    type: String,
    default: null,
  },
  initialTime: {
    type: String,
    default: null,
  },
  initialEndTime: {
    type: String,
    default: null,
  },
  initialDuration: {
    type: Number,
    default: null,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  preSelectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'created', 'updated']);

const store = useStore();
const { t } = useI18n();

// Form state
const selectedCalendarId = ref(null);
const title = ref('');
const appointmentTypeId = ref(null);
const contactId = ref(null);
const selectedContact = ref(null);
const agentId = ref(null);
const startsAt = ref('');
const endsAt = ref('');
const description = ref('');
const isSubmitting = ref(false);

const isEditing = computed(() => !!props.appointment);

// Get appointment types for the selected calendar
const appointmentTypes = computed(() => store.getters['calendar/getAppointmentTypes']);
const uiFlags = computed(() => store.getters['calendar/getUIFlags']);

// Get the selected calendar object
const selectedCalendar = computed(() => {
  if (!selectedCalendarId.value) return null;
  return props.calendars.find(c => c.id === selectedCalendarId.value) || null;
});

// Get contrasting color for calendar indicator
const getContrastColor = hexColor => {
  if (!hexColor) return '#ffffff';
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1e293b' : '#ffffff';
};

// Initialize form
const initForm = () => {
  if (props.appointment) {
    // Editing - use the appointment's calendar
    selectedCalendarId.value = props.appointment.calendar?.id || props.appointment.calendar_id || props.calendar?.id;
    title.value = props.appointment.title;
    appointmentTypeId.value = props.appointment.appointment_type?.id || props.appointment.appointment_type_id || null;
    contactId.value = props.appointment.contact?.id || props.appointment.contact_id || null;
    selectedContact.value = props.appointment.contact;
    agentId.value = props.appointment.agent?.id || props.appointment.agent_id || null;
    startsAt.value = formatDateTimeForInput(props.appointment.starts_at);
    endsAt.value = formatDateTimeForInput(props.appointment.ends_at);
    description.value = props.appointment.description || '';
  } else {
    // New appointment - use pre-selected calendar (from picker) or first selected
    if (props.calendar?.id) {
      // Pre-selected calendar from picker takes priority
      selectedCalendarId.value = props.calendar.id;
    } else {
      const storeSelectedCalendarIds = store.getters['calendar/getSelectedCalendarIds'];
      if (storeSelectedCalendarIds.length > 0) {
        selectedCalendarId.value = storeSelectedCalendarIds[0];
      } else if (props.calendars.length > 0) {
        selectedCalendarId.value = props.calendars[0].id;
      }
    }
    
    title.value = '';
    appointmentTypeId.value = null;
    agentId.value = props.agents[0]?.id || null;
    
    // Set pre-selected contact if provided
    if (props.preSelectedContact) {
      contactId.value = props.preSelectedContact.id;
      selectedContact.value = props.preSelectedContact;
    } else {
      contactId.value = null;
      selectedContact.value = null;
    }
    
    // Set initial date/time
    if (props.initialDate) {
      const date = props.initialDate;
      const time = props.initialTime || '09:00';
      
      // Parse time and create proper Date object for consistent formatting
      const [hours, minutes] = time.split(':').map(Number);
      const startDateTime = new Date(date);
      startDateTime.setHours(hours, minutes, 0, 0);
      startsAt.value = formatDateTimeForInput(startDateTime);
      
      if (props.initialEndTime) {
        const [endHours, endMinutes] = props.initialEndTime.split(':').map(Number);
        const endDateTime = new Date(date);
        endDateTime.setHours(endHours, endMinutes, 0, 0);
        endsAt.value = formatDateTimeForInput(endDateTime);
      } else {
        const durationMs = (props.initialDuration || 60) * 60 * 1000;
        const endDateTime = new Date(startDateTime.getTime() + durationMs);
        endsAt.value = formatDateTimeForInput(endDateTime);
      }
    } else {
      // No initial date provided - use current date and time
      const now = new Date();
      const roundedMinutes = now.getMinutes() < 30 ? 30 : 0;
      const roundedHours = now.getMinutes() < 30 ? now.getHours() : now.getHours() + 1;
      now.setHours(roundedHours, roundedMinutes, 0, 0);
      startsAt.value = formatDateTimeForInput(now);
      
      const endDateTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour duration
      endsAt.value = formatDateTimeForInput(endDateTime);
    }
    
    description.value = '';
  }
};

const formatDateTimeForInput = dateOrString => {
  if (!dateOrString) return '';

  // If it's a string, extract components directly from ISO format to avoid
  // browser timezone conversion (the backend sends times in account timezone)
  if (typeof dateOrString === 'string') {
    const match = dateOrString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`;
    }
  }

  // Fallback for Date objects (used by initForm for new appointments with browser-local dates)
  const date = dateOrString instanceof Date ? dateOrString : new Date(dateOrString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// When calendar changes, fetch its appointment types
watch(selectedCalendarId, async newCalendarId => {
  if (newCalendarId) {
    await store.dispatch('calendar/fetchAppointmentTypes', newCalendarId);
    // Reset appointment type selection when calendar changes
    if (!isEditing.value) {
      appointmentTypeId.value = null;
    }
  }
}, { immediate: false });

// Auto-update end date when start date changes
watch(startsAt, newStartsAt => {
  if (!newStartsAt) return;
  
  const currentStart = props.appointment?.starts_at 
    ? new Date(props.appointment.starts_at) 
    : null;
  const currentEnd = props.appointment?.ends_at 
    ? new Date(props.appointment.ends_at) 
    : null;
  
  let durationMs = 60 * 60 * 1000;
  if (currentStart && currentEnd) {
    durationMs = currentEnd - currentStart;
  } else if (endsAt.value && startsAt.value) {
    const prevStart = new Date(startsAt.value.replace('T', ' ').replace(/-/g, '/'));
    const prevEnd = new Date(endsAt.value.replace('T', ' ').replace(/-/g, '/'));
    if (!isNaN(prevStart) && !isNaN(prevEnd) && prevEnd > prevStart) {
      durationMs = prevEnd - prevStart;
    }
  }
  
  const newStartDate = new Date(newStartsAt);
  if (!isNaN(newStartDate)) {
    const newEndDate = new Date(newStartDate.getTime() + durationMs);
    endsAt.value = formatDateTimeForInput(newEndDate);
  }
}, { flush: 'sync' });

// Handle contact selection
const handleContactSelect = contact => {
  selectedContact.value = contact;
  contactId.value = contact?.id || null;
};

// Submit form
const handleSubmit = async () => {
  if (!selectedCalendarId.value) {
    useAlert(t('CALENDAR.ERRORS.NO_CALENDARS'));
    return;
  }
  
  if (!title.value || !startsAt.value || !endsAt.value) {
    useAlert(t('CALENDAR.ERRORS.REQUIRED_FIELDS'));
    return;
  }
  
  // Contact is required
  if (!contactId.value) {
    useAlert(t('CALENDAR.ERRORS.CONTACT_REQUIRED'));
    return;
  }

  isSubmitting.value = true;

  try {
    const data = {
      calendar_id: selectedCalendarId.value,
      title: title.value,
      appointment_type_id: appointmentTypeId.value,
      contact_id: contactId.value,
      agent_id: agentId.value,
      starts_at: startsAt.value,
      ends_at: endsAt.value,
      description: description.value,
    };
    
    console.log('[APPOINTMENT MODAL DEBUG] Submitting appointment:', data);

    if (isEditing.value) {
      await store.dispatch('calendar/updateAppointment', {
        appointmentId: props.appointment.id,
        data,
      });
      emit('updated');
      emit('close');
    } else {
      await store.dispatch('calendar/createAppointment', data);
      emit('created');
      emit('close');
    }
  } catch (error) {
    console.error('[APPOINTMENT MODAL DEBUG] Error creating/updating appointment:', error);
    console.error('[APPOINTMENT MODAL DEBUG] Error response:', error?.response?.data);
    
    // Show specific error message if available
    const errorMessage = error?.response?.data?.errors?.[0] || 
                         error?.response?.data?.message ||
                         t('CALENDAR.ERRORS.SAVE_FAILED');
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

// Watch for appointment types and set default
watch(
  () => appointmentTypes.value,
  types => {
    if (types && types.length > 0) {
      const typeIdFromAppointment = props.appointment?.appointment_type?.id || props.appointment?.appointment_type_id;
      if (isEditing.value && typeIdFromAppointment) {
        appointmentTypeId.value = typeIdFromAppointment;
      } else if (!appointmentTypeId.value && !isEditing.value) {
        appointmentTypeId.value = types[0].id;
      }
    }
  },
  { immediate: true }
);

// Watch for agents to initialize
watch(
  () => props.agents,
  agents => {
    if (agents && agents.length > 0) {
      const agentIdFromAppointment = props.appointment?.agent?.id || props.appointment?.agent_id;
      if (isEditing.value && agentIdFromAppointment && !agentId.value) {
        agentId.value = agentIdFromAppointment;
      } else if (!agentId.value && !isEditing.value) {
        agentId.value = agents[0]?.id || null;
      }
    }
  },
  { immediate: true }
);

onMounted(async () => {
  initForm();
  // Fetch appointment types for the selected calendar
  if (selectedCalendarId.value) {
    await store.dispatch('calendar/fetchAppointmentTypes', selectedCalendarId.value);
  }
});
</script>

<template>
  <Modal
    :show="true"
    @close="$emit('close')"
  >
    <div class="flex flex-col p-6">
      <h2 class="mb-6 text-lg font-semibold text-n-slate-12">
        {{ isEditing ? t('CALENDAR.MODAL.EDIT_APPOINTMENT') : t('CALENDAR.MODAL.NEW_APPOINTMENT') }}
      </h2>

      <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
        <!-- Calendar Selector (prominent at the top) -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
            {{ t('CALENDAR.SELECT_CALENDAR') }}
          </label>
          <div class="relative">
            <select
              v-model="selectedCalendarId"
              class="calendar-select w-full rounded-lg border-2 bg-n-alpha-1 py-3 pl-10 pr-10 text-sm font-semibold text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1"
              :style="{
                borderColor: selectedCalendar?.color || '#3b82f6',
                backgroundColor: selectedCalendar?.color ? `${selectedCalendar.color}15` : undefined,
              }"
            >
              <option
                v-for="cal in calendars"
                :key="cal.id"
                :value="cal.id"
              >
                {{ cal.name }}
              </option>
            </select>
            <!-- Calendar color indicator -->
            <div
              class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 rounded-full"
              :style="{ backgroundColor: selectedCalendar?.color || '#3b82f6' }"
            />
            <!-- Dropdown arrow -->
            <div class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
            </div>
          </div>
        </div>

        <!-- Title -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
            {{ t('CALENDAR.FORM.TITLE') }}
          </label>
          <Input
            v-model="title"
            :placeholder="t('CALENDAR.FORM.TITLE_PLACEHOLDER')"
            required
          />
        </div>

        <!-- Appointment Type -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
            {{ t('CALENDAR.FORM.TYPE') }}
          </label>
          <select
            v-model="appointmentTypeId"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none focus:ring-1 focus:ring-n-blue-9"
          >
            <option :value="null" disabled>
              {{ uiFlags.isFetchingAppointmentTypes ? 'Carregando...' : 'Selecione um tipo' }}
            </option>
            <option
              v-for="type in appointmentTypes"
              :key="type.id"
              :value="type.id"
            >
              {{ type.name }}
            </option>
          </select>
        </div>

        <!-- Contact Search -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
            {{ t('CALENDAR.FORM.CONTACT') }}
          </label>
          <ContactSearch
            :selected-contact="selectedContact"
            @select="handleContactSelect"
          />
        </div>

        <!-- Agent -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
            {{ t('CALENDAR.FORM.AGENT') }}
          </label>
          <select
            v-model="agentId"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none focus:ring-1 focus:ring-n-blue-9"
          >
            <option :value="null">
              {{ t('CALENDAR.FORM.NO_AGENT') }}
            </option>
            <option
              v-for="agent in agents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </div>

        <!-- Date/Time -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
              {{ t('CALENDAR.FORM.START') }}
            </label>
            <input
              v-model="startsAt"
              type="datetime-local"
              required
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none focus:ring-1 focus:ring-n-blue-9"
            />
          </div>
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
              {{ t('CALENDAR.FORM.END') }}
            </label>
            <input
              v-model="endsAt"
              type="datetime-local"
              required
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-9 focus:outline-none focus:ring-1 focus:ring-n-blue-9"
            />
          </div>
        </div>

        <!-- Description -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-n-slate-12">
            {{ t('CALENDAR.FORM.DESCRIPTION') }}
          </label>
          <textarea
            v-model="description"
            :placeholder="t('CALENDAR.FORM.DESCRIPTION_PLACEHOLDER')"
            rows="3"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-9 focus:outline-none focus:ring-1 focus:ring-n-blue-9"
          />
        </div>

        <!-- Actions -->
        <div class="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            :label="t('CALENDAR.ACTIONS.CANCEL')"
            @click="$emit('close')"
          />
          <Button
            type="submit"
            variant="solid"
            color-scheme="primary"
            :label="isEditing ? t('CALENDAR.ACTIONS.UPDATE') : t('CALENDAR.ACTIONS.CREATE')"
            :is-loading="isSubmitting"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style>
/* Hide native select arrow in all browsers */
.calendar-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none;
}

/* Additional fix for IE */
.calendar-select::-ms-expand {
  display: none;
}
</style>
